import React from "react";
import PreviewPaper from "./PreviewPaper";
import { EXPENSE_TEMPLATES } from "./expenseTemplates";

/* ── 숫자 → 한글 (억/만 단위 올바르게 처리) ────────────────────
   기존 income 쪽 함수는 10만 이상에서 틀어져서(예: 팔십만이만…) 여기서 새로 짬. */
function numberToKorean(num) {
  num = Number(num);
  if (!num) return "영";
  const d = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"];
  const s = ["", "십", "백", "천"];
  const b = ["", "만", "억", "조"];
  let result = "";
  let gi = 0;
  while (num > 0) {
    const group = num % 10000;
    if (group > 0) {
      let str = "";
      let g = group;
      let pos = 0;
      while (g > 0) {
        const digit = g % 10;
        if (digit > 0) str = d[digit] + s[pos] + str;
        g = Math.floor(g / 10);
        pos++;
      }
      result = str + b[gi] + result;
    }
    num = Math.floor(num / 10000);
    gi++;
  }
  return result;
}

/* ── 공통 규칙: 금액 50만원 이상이면 붙임이 산출기초조사서/견적서로 자동 전환된다.
   템플릿 종류와 무관하게 모든 지출 템플릿에 적용되므로, 각 템플릿에 반복 정의하지 않고
   여기 한 곳(Attachments 렌더 직전)에서만 처리한다. ────────────────── */
const HIGH_AMOUNT_ATTACHMENTS = [
  { name: "산출기초조사서", count: 1 },
  { name: "견적서(타견적서)", count: 1 },
];
// ✅ 급여류 템플릿(임직원급여 + 카페인 4개 지점 급여지급)은 물품/용역 구매가 아니라
// 인건비 지급이므로 견적서·산출기초조사서가 성립하지 않는다 — 50만원 규칙 적용 제외.
const HIGH_AMOUNT_ATTACHMENT_EXEMPT_TEMPLATES = [
  "payroll",
  "gasan_payroll",
  "doksan_payroll",
  "geumnarae_payroll",
  "green_payroll",
];
function resolveAttachments(templateCode, amount, defaultItems) {
  if (HIGH_AMOUNT_ATTACHMENT_EXEMPT_TEMPLATES.includes(templateCode)) return defaultItems;
  return (Number(amount) || 0) >= 500000 ? HIGH_AMOUNT_ATTACHMENTS : defaultItems;
}

/* ── 붙임 (기존 income 미리보기와 동일 규칙) ──────────────────── */
function Attachments({ items }) {
  const list = (items || []).filter((a) => a.name && a.name.trim() !== "");
  if (list.length === 0) return null;

  if (list.length === 1) {
    return (
      <div className="content-attachment">
        <span className="attachment-label">붙임</span>
        <span className="attachment-text">
          &nbsp;&nbsp;{list[0].name} {list[0].count}부.&nbsp;&nbsp;끝.
        </span>
      </div>
    );
  }

  return (
    <div className="content-attachment">
      <span className="attachment-label">붙임</span>
      <span className="attachment-lines">
        {list.map((a, i) => (
          <span key={i} className="attachment-line">
            &nbsp;&nbsp;{i + 1}. {a.name} {a.count}부.
            {i === list.length - 1 && <>&nbsp;&nbsp;끝.</>}
          </span>
        ))}
      </span>
    </div>
  );
}

/* ── 항목 라벨 콜론 정렬 ────────────────────────────────────────
   라벨 길이가 제각각(예: "건명" 2자 vs "예산과목" 4자)이라 콜론 위치가
   안 맞는 문제 — 라벨 문자열에 공백을 수작업으로 끼워 넣는 대신, 해당
   템플릿의 fields 배열에서 가장 긴 라벨(공백 제거 기준) 글자 수를 구해
   모든 라벨을 그 폭(em)에 균등 배분한다. 템플릿마다
   라벨 구성이 다르므로 폭은 템플릿별로 동적으로 계산 — 특정 템플릿에
   하드코딩하지 않는다. */
function coreLabelText(label) {
  return (label || "").replace(/\s+/g, "");
}

// ⚠ text-align-last: justify는 "건명"처럼 공백 없는 한 단어는 분산시킬
// 지점이 없어 동작하지 않는다(1차 시도 버그). 글자를 개별 span으로 쪼개
// flex + justify-content: space-between으로 강제 분산 — 공백 문자·폰트
// 종류와 무관하게 항상 동작. 1글자 라벨은 분산 지점이 없으므로 중앙 배치.
function JustifiedFieldLabel({ label, maxLen }) {
  const chars = coreLabelText(label).split("");
  return (
    // ⚠ 공용 CSS의 .detail-label{width:85px}는 4자 기준 폭이라, maxLen이 이보다
    // 큰 템플릿(예: 7자 "사회보험료 내역")에서 내용이 넘쳐 값 컬럼과 겹친다 —
    // 폭을 라벨 내용에 맞춰 auto로 풀어주고 실제 폭은 안쪽 flex span이 결정.
    <span className="detail-label" style={{ width: "auto" }}>
      <span
        className="detail-label-core"
        style={{
          display: "inline-flex",
          width: `${maxLen}em`,
          justifyContent: chars.length <= 1 ? "center" : "space-between",
          alignItems: "baseline",
        }}
      >
        {chars.map((ch, i) => (
          <span key={i}>{ch}</span>
        ))}
      </span>
      {/* 값과의 간격을 공백 문자가 아니라 margin으로 — 폰트 무관하게 일관됨 */}
      <span style={{ marginRight: "0.5em" }}>{" :"}</span>
    </span>
  );
}

/* ── 필드 1개 렌더링 — 이 함수가 template.fields 를 그린다 ────────── */
function renderField(field, num, value, maxLen) {
  // 표
  if (field.type === "table") {
    const rows = value?.rows || [];
    const hasHeader = field.columns && field.columns.length > 0;
    const showUnit = field.showUnit !== false; // 기본 유지(신용카드/채용심사비), false로 명시한 템플릿만 숨김
    return (
      <div key={field.key}>
        <div className="detail-item">
          <span className="detail-number">{num}.</span>
          <JustifiedFieldLabel label={field.label} maxLen={maxLen} />
        </div>
        <div
          className={`claim-table-wrap${field.infoTable ? " claim-table--info" : ""}${
            field.deptTable ? " claim-table--dept" : ""
          }`}
        >
          {showUnit && (
            <div style={{ textAlign: "right", fontSize: "13px", margin: "4px 0" }}>(단위 : 원)</div>
          )}
          <table className="claim-table">
            {hasHeader && (
              <thead>
                <tr>
                  {field.columns.map((c, i) => {
                    const isSpan = c && typeof c === "object";
                    return (
                      <th key={i} colSpan={isSpan ? c.colSpan : undefined}>
                        {isSpan ? c.label : c}
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((cell, ci) => {
                    const isSpan = cell && typeof cell === "object";
                    const style = isSpan
                      ? {
                          ...(cell.align ? { textAlign: cell.align } : {}),
                          ...(cell.bold ? { fontWeight: "bold" } : {}),
                        }
                      : undefined;
                    return (
                      <td
                        key={ci}
                        colSpan={isSpan ? cell.colSpan : undefined}
                        rowSpan={isSpan ? cell.rowSpan : undefined}
                        className={ci === 0 ? "" : "amount-cell"}
                        style={style}
                      >
                        {isSpan ? cell.value : cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // 텍스트 / 금액
  const text =
    field.type === "money"
      ? value
        ? `금${Number(value).toLocaleString()}원(금${numberToKorean(value)}원)`
        : "-"
      : value ?? "-";

  return (
    <div className="detail-item" key={field.key}>
      <span className="detail-number">{num}.</span>
      <JustifiedFieldLabel label={field.label} maxLen={maxLen} />
      <span
        className={`detail-text${field.type === "money" ? " amount" : ""}`}
        style={field.multiline ? { whiteSpace: "pre-line" } : undefined}
      >
        {text}
      </span>
    </div>
  );
}

/**
 * 지출 미리보기.
 * IncomeProposalPreview 처럼 통째로 만드는 게 아니라,
 * 공용 껍데기(PreviewPaper) + 템플릿 기반 본문 = "조합"일 뿐이다.
 * 지출 유형이 아무리 늘어도 이 파일은 안 바뀌고 expenseTemplates.js 만 늘어난다.
 *
 * props:
 *   templateCode   : "credit_card" 등 (EXPENSE_TEMPLATES 키)
 *   values         : { title, amount, vendor, cardNo, month, detail:{rows:[...]}, attachments? }
 *   documentNumber : 시행번호
 *   approvalLine   : 결재선
 *   onSave, saving : 저장 버튼
 */
export default function ExpenseProposalPreview({
  templateCode,
  values = {},
  documentNumber,
  approvalLine = [],
  onSave,
  saving = false,
  extraActions,
  hidePreviewButton = false,
}) {
  const template = EXPENSE_TEMPLATES[templateCode];
  const maxLen = template
    ? Math.max(...template.fields.map((f) => coreLabelText(f.label).length))
    : 0;

  const body = !template ? (
    <div className="content-intro">
      <p>템플릿을 선택하세요.</p>
    </div>
  ) : (
    <>
      <div className="content-intro">
        <p>{typeof template.intro === "function" ? template.intro(values) : template.intro}</p>
      </div>
      <div className="content-details">
        {template.fields.map((f, i) => renderField(f, i + 1, values[f.key], maxLen))}
      </div>
      <Attachments items={resolveAttachments(templateCode, values.amount, values.attachments || template.attachments)} />
    </>
  );

  return (
    <PreviewPaper
      title={values.title}
      documentNumber={documentNumber}
      approvalLine={approvalLine}
      onSave={onSave}
      saving={saving}
      extraActions={extraActions}
      hidePreviewButton={hidePreviewButton}
    >
      {body}
    </PreviewPaper>
  );
}